/**
 * Renders user-created scene objects (primitives) in the 3D viewport.
 *
 * In object mode, these behave like blend-file objects: selectable and rendered
 * with the current shading mode.
 *
 * In edit mode, the object being edited renders its true material as the base,
 * with a near-opaque gray overlay on top so the original material faintly bleeds
 * through. EditModeRenderer handles vertex display and manipulation.
 *
 * All wireframe overlays use polygon-edge LineSegments (no triangle diagonals).
 */

import { type ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineSegments2 } from "three/examples/jsm/lines/webgpu/LineSegments2.js";
import * as THREE from "three/webgpu";
import { acceleratedRaycast } from "three-mesh-bvh";
import {
  applySceneObjectWorldTransformToObject3D,
  buildSceneObjectWorldNameMap,
  composeSceneObjectWorldTransform,
  getCollectionForObject,
  getSceneObjectWorldTransform,
  worldTransformForSceneObject,
} from "../app/collectionGraph.ts";
import { resolveSceneObjectEditSession } from "../app/editSession.ts";
import { reportInvalidSceneObject } from "../app/invalidSceneObject.ts";
import { useActiveRenderConfig } from "../app/renderConfig.ts";
import { useStore } from "../app/store.ts";
import type {
  AppState,
  Collection,
  ModifierOverride,
} from "../app/storeTypes.ts";
import { useLiveEditPreview } from "../app/useLiveEditPreview.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import { isVisibleInWorkspace } from "../app/visibilityPolicy.ts";
import type { BlendFollowObjectModifier } from "../blend-reader/types/BlendModifier.ts";
import { getBone } from "../mannequin/boneRegistry.ts";
import { evaluateCurve } from "../modeling/curvemath.ts";
import { expandForRendering } from "../modeling/loopExpand.ts";
import { resolveAutoSmoothAngle } from "../modeling/meshOps.ts";
import { validateSceneObject } from "../modeling/sceneObjectValidation.ts";
import { getWireEdgePairs } from "../modeling/topology.ts";
import type {
  CurveData,
  LightKind,
  SceneMaterial,
  SceneObject,
} from "../modeling/types.ts";
import {
  DEFAULT_MATERIAL,
  isCurveObject,
  isMannequinObject,
  resolveFaceMaterialIndices,
} from "../modeling/types.ts";
import { consumeDirtySculptUpdate } from "../sculpt/sculptOps.ts";
import { useAsyncBoundsTree } from "./asyncBoundsTree.ts";
import { addComponentUpdateRanges } from "./bufferUpdateRanges.ts";
import {
  createViewportMaterial,
  sceneMaterialToConfig,
} from "./createViewportMaterial.ts";
import { getCachedTriToPolyMap } from "./editMeshCache.ts";
import {
  clearConstrainedPosition,
  setConstrainedPosition,
} from "./followConstraintRegistry.ts";
import { getDefaultGridProjection } from "./gridOverlayNode.ts";
import {
  HIDDEN_SELECTION_GHOST_MAT,
  HIDDEN_SELECTION_GHOST_OPACITY,
  HIDDEN_SELECTION_GHOST_RENDER_ORDER,
  shouldShowHiddenSelectionGhost,
} from "./hiddenSelectionGhost.ts";
import { useDispose } from "./hooks/useDispose.ts";
import { markInkingCategory } from "./inkingCategories.ts";
import { useMannequinSurfaceSnapshots } from "./mannequinSurfaceSnapshots.ts";
import { MATCAP_PRESETS } from "./matcapPresets.ts";
import { buildModifierEvaluationRequest } from "./modifierApply.ts";
import { sceneObjectGeometryCacheKey } from "./modifiers/cacheKeys.ts";
import {
  buildSceneObjectNameMap,
  mergeModifierStack,
  modifierStackHasEnabledInstance,
  modifierStackNeedsOwnerWorldTransform,
  modifierStackNeedsSceneContext,
} from "./modifiers/evaluation.ts";
import type { ModifierGeometry } from "./modifiers/modifierTypes.ts";
import {
  applyFaceMaterialGroups,
  buildRenderModifierGeometry,
  finalizePerCorner,
  topologyForModifierGeometry,
} from "./modifiers/renderGeometry.ts";
import { useModifierEvaluation } from "./modifiers/useModifierEvaluation.ts";
import {
  buildPolylineSegmentPositions,
  createObjectHelperLineMaterial,
  OBJECT_HELPER_LINEWIDTH,
  setFatLinePositions,
  WIRE_OBJECT_LINEWIDTH,
} from "./objectHelperLines.ts";
import {
  getCurveHelperColor,
  getEmptyHelperColor,
  getWireObjectColor,
} from "./objectHelperStyles.ts";
import { PolyWireframe } from "./PolyWireframe.tsx";
import { findAsymmetricTopologyEdges } from "./primitiveSymmetryDebug.ts";
import { resolveRealtimeLightShadowSettings } from "./realtimeShadows.ts";
import { SkinRadiusOverlay } from "./SkinRadiusOverlay.tsx";
import { SymmetryPlaneOverlay } from "./SymmetryPlaneOverlay.tsx";
import { makeScratch } from "./scratch.ts";
import { screenToWorldRadius } from "./screenRadius.ts";
import {
  getActiveSelectedObjectIdFromState,
  getSelectionPrepassMaterial,
  useSelectionOutlineLayer,
} from "./selectionOutlinePass.ts";
import {
  EDIT_OVERLAY_OPACITY,
  noRaycast,
  SCULPT_DIM_MAT,
  SURFACE_XRAY_OPACITY,
  WIREFRAME_DEPTH_PREPASS_MAT,
} from "./sharedMaterials.ts";
import { useObjectTap } from "./useObjectTap.ts";
import { useViewportShading } from "./useViewportShading.ts";
import { viewportSupportsSceneInteraction } from "./viewportInteractionPolicy.ts";
import { WireEdgeHitMesh } from "./WireEdgeHitMesh.tsx";

// ---------------------------------------------------------------------------
// Follow Object constraint — module-level temp objects (never reallocated)
// ---------------------------------------------------------------------------

const follow = makeScratch({
  pos: "vec3",
  quat: "quat",
  quat2: "quat",
  scale: "vec3",
});

const EMPTY_SCENE_OBJECTS = new Map<string, SceneObject>();
const EMPTY_COLLECTIONS = new Map<string, Collection>();
const EMPTY_MODIFIER_OVERRIDES = new Map<string, readonly ModifierOverride[]>();
const IDENTITY_POSITION: [number, number, number] = [0, 0, 0];
const IDENTITY_ROTATION: [number, number, number] = [0, 0, 0];
const IDENTITY_SCALE: [number, number, number] = [1, 1, 1];
const IDENTITY_WORLD_TRANSFORM = {
  position: IDENTITY_POSITION,
  rotation: IDENTITY_ROTATION,
  scale: IDENTITY_SCALE,
};

type PrimitiveCommandPhase = "idle" | "objectTransform" | "otherCommand";

function selectPrimitiveCommandPhase(
  state: Pick<
    AppState,
    "commandBuffer" | "mode" | "workspaceMode" | "editMode" | "poseMode"
  >,
): PrimitiveCommandPhase {
  const cb = state.commandBuffer;
  if (!cb) return "idle";
  return state.mode === "transform" &&
    state.workspaceMode === "object" &&
    state.editMode === null &&
    state.poseMode === null &&
    (cb.type === "translate" || cb.type === "rotate" || cb.type === "scale")
    ? "objectTransform"
    : "otherCommand";
}

function syncExpandedPositionAttribute(
  geometry: THREE.BufferGeometry,
  vertices: Float32Array,
  topology: SceneObject["topology"],
  dirtyVertices: Uint32Array | null,
  dirtyVertexPositions: Float32Array | null,
  vtxToCorners: { offsets: Uint32Array; data: Uint32Array },
  loopNormals: SceneObject["loopNormals"],
  uvs?: Float32Array,
): void {
  const { corners } = topology;
  const posAttr = geometry.getAttribute("position") as THREE.BufferAttribute;
  const posArr = posAttr.array as Float32Array;
  if (posArr.length === corners.length * 3) {
    if (dirtyVertices && dirtyVertices.length > 0) {
      const { offsets: vtcOffsets, data: vtcData } = vtxToCorners;
      const touchedCorners: number[] = [];
      for (let i = 0; i < dirtyVertices.length; i++) {
        const vi = dirtyVertices[i]!;
        const src = dirtyVertexPositions ? i * 3 : vi * 3;
        const vx = dirtyVertexPositions
          ? dirtyVertexPositions[src]!
          : vertices[src]!;
        const vy = dirtyVertexPositions
          ? dirtyVertexPositions[src + 1]!
          : vertices[src + 1]!;
        const vz = dirtyVertexPositions
          ? dirtyVertexPositions[src + 2]!
          : vertices[src + 2]!;
        for (let j = vtcOffsets[vi]; j < vtcOffsets[vi + 1]; j++) {
          const c = vtcData[j]!;
          posArr[c * 3] = vx;
          posArr[c * 3 + 1] = vy;
          posArr[c * 3 + 2] = vz;
          touchedCorners.push(c);
        }
      }
      if (touchedCorners.length === 0) return;
      addComponentUpdateRanges(posAttr, touchedCorners, 3);
    } else {
      for (let c = 0; c < corners.length; c++) {
        const vi = corners[c];
        posArr[c * 3] = vertices[vi * 3];
        posArr[c * 3 + 1] = vertices[vi * 3 + 1];
        posArr[c * 3 + 2] = vertices[vi * 3 + 2];
      }
      posAttr.updateRanges.length = 0;
    }
    posAttr.needsUpdate = true;
    return;
  }

  const expanded = expandForRendering(vertices, loopNormals, topology, uvs);
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(expanded.renderVertices, 3),
  );
}

function LiveEditGeometrySync({
  objectId,
  geometry,
  topology,
  loopNormals,
  uvs,
  vtxToCorners,
  disabled,
  vertices,
}: {
  objectId: string | null;
  geometry: THREE.BufferGeometry;
  vertices: Float32Array;
  topology: SceneObject["topology"];
  loopNormals: SceneObject["loopNormals"];
  uvs?: Float32Array;
  vtxToCorners: { offsets: Uint32Array; data: Uint32Array };
  disabled: boolean;
}) {
  const { invalidate } = useThree();
  const liveEditPreview = useLiveEditPreview(objectId);

  useLayoutEffect(() => {
    if (
      disabled ||
      !objectId ||
      !geometry.index ||
      (!liveEditPreview?.vertices && !liveEditPreview?.vertexPatch)
    ) {
      return;
    }
    syncExpandedPositionAttribute(
      geometry,
      liveEditPreview.vertices ?? vertices,
      topology,
      liveEditPreview.dirtyVertices ??
        liveEditPreview.vertexPatch?.indices ??
        null,
      liveEditPreview.vertexPatch?.positions ?? null,
      vtxToCorners,
      loopNormals,
      uvs,
    );
    invalidate();
  }, [
    disabled,
    objectId,
    vertices,
    geometry,
    liveEditPreview,
    loopNormals,
    topology,
    uvs,
    vtxToCorners,
    invalidate,
  ]);

  return null;
}

export function shouldShowGeneratorWireframeOverlay({
  isBeingEdited,
  showWireframe,
  generatorAdjusting,
  isGeneratorBacked,
  viewMode,
}: {
  isBeingEdited: boolean;
  showWireframe: boolean;
  generatorAdjusting: boolean;
  isGeneratorBacked: boolean;
  viewMode: "matcap" | "material" | "wireframe";
}): boolean {
  return (
    !isBeingEdited &&
    (showWireframe || (generatorAdjusting && isGeneratorBacked)) &&
    viewMode !== "wireframe"
  );
}

// ---------------------------------------------------------------------------
// Single primitive mesh
// ---------------------------------------------------------------------------

export function PrimitiveNode({ obj }: { obj: SceneObject }) {
  const invalidObject = useMemo(() => validateSceneObject(obj), [obj]);

  useEffect(() => {
    if (invalidObject) reportInvalidSceneObject(obj, invalidObject);
  }, [invalidObject, obj]);

  if (invalidObject) return null;

  return <PrimitiveNodeInner obj={obj} />;
}

function PrimitiveNodeInner({ obj }: { obj: SceneObject }) {
  const selected = useSelectedLeafObjectIds().has(obj.id);
  const activeSelectedObjectId = useStore(getActiveSelectedObjectIdFromState);
  const visible = useStore((s) => s.visibility.get(obj.id) ?? true);
  const renderVisible = useStore((s) => s.renderVisibility.get(obj.id) ?? true);
  const curveObject = isCurveObject(obj);
  const {
    viewMode,
    matcapPreset,
    shadowsEnabled,
    showWireframe,
    objectXray,
    wireframeXray,
    showTextures,
    textureMapsEnabled,
    workspaceMode,
  } = useViewportShading();
  const effectiveVisible = isVisibleInWorkspace({
    workspaceMode,
    viewportVisible: visible,
    renderVisible,
  });
  const showSceneInteraction = viewportSupportsSceneInteraction(workspaceMode);
  const primitiveSymmetryDebugAxis = useStore(
    (s) => s.primitiveSymmetryDebugAxis,
  );
  const editMode = useStore((s) => s.editMode);
  const objModifierOverrides = useStore((s) => s.modifierOverrides.get(obj.id));
  useStore((s) => s.shadingOverrides.get(obj.id));
  // Resolve every slot's material via a stable Map subscription, then flatten
  // to an array keyed on the slot list. Subscribing to the registry Map (not
  // a materialized array) avoids the new-reference-every-render trap that
  // Zustand selectors have when they allocate. `useMemo` then keeps the array
  // identity stable so downstream memos don't invalidate unnecessarily.
  const materialsMap = useStore((s) => s.materials);
  const slotMaterials: SceneMaterial[] = useMemo(() => {
    const slots = obj.materialSlots;
    const out: SceneMaterial[] = new Array(slots.length);
    for (let i = 0; i < slots.length; i++) {
      out[i] =
        materialsMap.get(slots[i].materialId)?.properties ?? DEFAULT_MATERIAL;
    }
    return out;
  }, [obj.materialSlots, materialsMap]);
  // Single-material fallback used by edit-mode / wireframe overrides and by
  // the rest of this component where slot 0 is the correct fallback.
  const resolvedMaterial: SceneMaterial = slotMaterials[0] ?? DEFAULT_MATERIAL;

  const isBeingEdited = editMode?.objectId === obj.id;
  const showHiddenSelectionGhost =
    !isBeingEdited &&
    shouldShowHiddenSelectionGhost({
      selected,
      effectiveVisible,
      showSceneInteraction,
    });
  const storeEditSession = useStore((s) =>
    isBeingEdited && s.activeEditSession?.objectId === obj.id
      ? s.activeEditSession
      : null,
  );
  const activeEditSession = storeEditSession;
  const renderObj = useMemo(
    () => resolveSceneObjectEditSession(obj, activeEditSession),
    [obj, activeEditSession],
  );
  const dirtyEditVertices = activeEditSession?.dirtyVertices ?? null;
  const hasDirtyEditVertices =
    dirtyEditVertices !== null && dirtyEditVertices.length > 0;
  const editXray = isBeingEdited && (editMode?.xray ?? false);
  const objectXrayActive = workspaceMode === "object" && objectXray;
  const effectiveWireframeXray = wireframeXray || objectXrayActive;
  const hiddenFaces = useStore((s) =>
    s.editMode?.objectId === obj.id ? s.editMode.hiddenFaces : undefined,
  );

  const groupRef = useRef<THREE.Group>(null);
  const meshRef = useRef<THREE.Mesh>(null);
  const selectionPrepassRef = useRef<THREE.Mesh>(null);

  // Per-frame transform sync — reads the latest transform directly from the
  // store, bypassing React's render batching.  This ensures the viewport mesh
  // is never a frame behind the data layer during live gizmo drags / snapping.
  // Follow Object constraints are applied on top: the stored position/rotation/scale
  // acts as a world-space offset from the target bone or object root.
  const objId = obj.id;
  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    const latestState = useStore.getState();
    if (
      !composeSceneObjectWorldTransform(
        latestState.sceneObjects,
        latestState.collections,
        objId,
        follow.pos,
        follow.quat,
        follow.scale,
      )
    ) {
      return;
    }
    let px = follow.pos.x;
    let py = follow.pos.y;
    let pz = follow.pos.z;
    let sx = follow.scale.x;
    let sy = follow.scale.y;
    let sz = follow.scale.z;

    const constraints = followConstraintsRef.current;
    if (constraints.length > 0) {
      const allObjs = latestState.sceneObjects;
      const allCollections = latestState.collections;
      const allObjectsByName = buildSceneObjectNameMap(allObjs);
      for (const mod of constraints) {
        const targetObjectName = mod.targetObjectName;
        if (!targetObjectName) continue;
        const target = allObjectsByName.get(targetObjectName);
        if (!target) continue;

        if (mod.targetBoneName) {
          // Mannequin bone target — world transforms via Three.js bone.
          const bone = getBone(target.id, mod.targetBoneName);
          if (!bone) continue;
          if (mod.followPosition) {
            bone.getWorldPosition(follow.pos);
            px += follow.pos.x;
            py += follow.pos.y;
            pz += follow.pos.z;
          }
          if (mod.followRotation) {
            bone.getWorldQuaternion(follow.quat2);
            follow.quat.premultiply(follow.quat2);
          }
          if (mod.followScale) {
            bone.getWorldScale(follow.scale);
            sx *= follow.scale.x;
            sy *= follow.scale.y;
            sz *= follow.scale.z;
          }
        } else {
          // Object root target — read directly from store.
          if (
            !composeSceneObjectWorldTransform(
              allObjs,
              allCollections,
              target.id,
              follow.pos,
              follow.quat2,
              follow.scale,
            )
          ) {
            continue;
          }
          if (mod.followPosition) {
            px += follow.pos.x;
            py += follow.pos.y;
            pz += follow.pos.z;
          }
          if (mod.followRotation) {
            follow.quat.premultiply(follow.quat2);
          }
          if (mod.followScale) {
            sx *= follow.scale.x;
            sy *= follow.scale.y;
            sz *= follow.scale.z;
          }
        }
      }
    }

    g.position.set(px, py, pz);
    g.quaternion.copy(follow.quat);
    g.scale.set(sx, sy, sz);

    // Publish constrained world position so TransformGizmo can track the visual
    // mesh position instead of the stored offset when follow constraints are active.
    if (constraints.length > 0) {
      setConstrainedPosition(objId, px, py, pz);
    } else {
      clearConstrainedPosition(objId);
    }
  });

  // Clear the registry entry when this object is removed from the scene.
  useEffect(() => () => clearConstrainedPosition(objId), [objId]);

  // Tap → select. Double-tap in object mode enters edit mode (mirrors the
  // mannequin double-tap-to-pose gesture). The double-tap window is per-object
  // and doesn't interfere with same-spot click-through cycling, which uses a
  // module-level cycle state rather than this component's tap timestamps.
  const enterEditMode = useStore((s) => s.enterEditMode);
  const isObjectMode = useStore((s) => s.workspaceMode === "object");
  const doubleTapEdit = useCallback(
    () => enterEditMode(obj.id),
    [enterEditMode, obj.id],
  );
  const {
    handlePointerDown,
    handlePointerUp,
    handlePointerEnter,
    handlePointerLeave,
  } = useObjectTap(obj.id, isObjectMode ? doubleTapEdit : undefined);
  const eyedropperHovered = useStore(
    (s) => s.eyedropperSession !== null && s.eyedropperHoveredId === obj.id,
  );

  // Resolve modifiers for this object.
  // Sculpt and sketch operate on the authored cage, not a derived modifier
  // result. Suspend geometry modifiers on the active target so the displayed
  // mesh matches the buffers those tools actually edit.
  const isSculptTarget = useStore((s) => s.sculptMode?.objectId === obj.id);
  const isSculptDimmed = useStore(
    (s) => s.sculptMode !== null && s.sculptMode.objectId !== obj.id,
  );
  // Sketch mode is no longer bound to a specific object — every sketchable
  // mesh can receive projected points. Suspend modifiers across the board
  // while sketch mode is active so the displayed mesh always matches the
  // authored cage.
  const isSketchTarget = useStore((s) => s.sketchMode !== null);
  const commandPhase = useStore(selectPrimitiveCommandPhase);
  const isCommandActive = commandPhase !== "idle";
  const curveHelperColor = getCurveHelperColor(selected, showSceneInteraction);
  const wireLineColor = getWireObjectColor({
    selected,
    showSelectionState: showSceneInteraction,
  });
  const worldTransform = useMemo(
    () =>
      getSceneObjectWorldTransform(
        useStore.getState().sceneObjects,
        useStore.getState().collections,
        obj.id,
      ) ?? {
        position: obj.position,
        rotation: obj.rotation,
        scale: obj.scale,
      },
    [obj.id, obj.position, obj.rotation, obj.scale],
  );
  const mergedModifiers = useMemo(
    () => mergeModifierStack([], objModifierOverrides ?? []),
    [objModifierOverrides],
  );
  const suspendGeometryModifiers = isSculptTarget || isSketchTarget;
  const filteredModifiers = useMemo(
    () =>
      mergedModifiers.filter(
        (modifier) =>
          !suspendGeometryModifiers || modifier.kind === "followObject",
      ),
    [mergedModifiers, suspendGeometryModifiers],
  );

  // Follow Object constraints are transform-level — strip them from the geometry
  // pipeline so their presence doesn't trigger unnecessary geometry rebuilds.
  const geometryModifiers = useMemo(
    () => filteredModifiers.filter((m) => m.kind !== "followObject"),
    [filteredModifiers],
  );
  // Edit mode works on the authored cage, so the shaded surface must stop at
  // the base mesh while the transform-only follow constraints stay live.
  const displayedGeometryModifiers = useMemo(
    () => (isBeingEdited ? [] : geometryModifiers),
    [isBeingEdited, geometryModifiers],
  );
  const objectTransformPreviewActive = commandPhase === "objectTransform";
  // Object-mode drags already move the rendered group imperatively every frame.
  // Freeze modifier evaluation inputs during the drag so dense modifier stacks
  // don't resubmit worker jobs for each pointer tick; confirm releases the
  // freeze and refreshes dependency-aware modifiers against the final transform.
  const modifierSourceObjRef = useRef(obj);
  const modifierSourceObj = objectTransformPreviewActive
    ? modifierSourceObjRef.current
    : obj;
  const needsModifierSceneContext = useMemo(
    () => modifierStackNeedsSceneContext(displayedGeometryModifiers),
    [displayedGeometryModifiers],
  );
  const needsModifierOwnerWorldTransform = useMemo(
    () => modifierStackNeedsOwnerWorldTransform(displayedGeometryModifiers),
    [displayedGeometryModifiers],
  );
  const liveModifierSceneObjects = useStore((s) =>
    needsModifierSceneContext && !objectTransformPreviewActive
      ? s.sceneObjects
      : EMPTY_SCENE_OBJECTS,
  );
  const liveModifierCollections = useStore((s) =>
    needsModifierOwnerWorldTransform && !objectTransformPreviewActive
      ? s.collections
      : EMPTY_COLLECTIONS,
  );
  const allModifierOverrides = useStore((s) =>
    needsModifierSceneContext ? s.modifierOverrides : EMPTY_MODIFIER_OVERRIDES,
  );
  const modifierSceneObjectsRef = useRef<ReadonlyMap<string, SceneObject>>(
    liveModifierSceneObjects,
  );
  const modifierCollectionsRef = useRef<ReadonlyMap<string, Collection>>(
    liveModifierCollections,
  );
  useLayoutEffect(() => {
    if (objectTransformPreviewActive) return;
    modifierSourceObjRef.current = obj;
    if (needsModifierSceneContext) {
      modifierSceneObjectsRef.current = liveModifierSceneObjects;
    }
    if (needsModifierOwnerWorldTransform) {
      modifierCollectionsRef.current = liveModifierCollections;
    }
  }, [
    objectTransformPreviewActive,
    obj,
    needsModifierSceneContext,
    needsModifierOwnerWorldTransform,
    liveModifierSceneObjects,
    liveModifierCollections,
  ]);
  const modifierSceneObjects = objectTransformPreviewActive
    ? modifierSceneObjectsRef.current
    : liveModifierSceneObjects;
  const modifierCollections = objectTransformPreviewActive
    ? modifierCollectionsRef.current
    : liveModifierCollections;
  const sceneObjectsByName = useMemo(
    () =>
      needsModifierSceneContext
        ? needsModifierOwnerWorldTransform
          ? buildSceneObjectWorldNameMap(
              modifierSceneObjects,
              modifierCollections,
            )
          : buildSceneObjectNameMap(modifierSceneObjects)
        : EMPTY_SCENE_OBJECTS,
    [
      needsModifierSceneContext,
      needsModifierOwnerWorldTransform,
      modifierSceneObjects,
      modifierCollections,
    ],
  );
  const mannequinSurfaceTargetIds = useMemo(() => {
    if (!needsModifierSceneContext) return [];
    const ids: string[] = [];
    for (const modifier of displayedGeometryModifiers) {
      if (
        !modifier.enabled ||
        modifier.kind !== "shrinkwrap" ||
        !modifier.targetObjectName
      ) {
        continue;
      }
      const target = sceneObjectsByName.get(modifier.targetObjectName);
      if (target && isMannequinObject(target)) ids.push(target.id);
    }
    return ids;
  }, [
    needsModifierSceneContext,
    displayedGeometryModifiers,
    sceneObjectsByName,
  ]);
  const mannequinSurfaceSnapshots = useMannequinSurfaceSnapshots(
    mannequinSurfaceTargetIds,
  );
  const runtimeSourceGeometries = useMemo(() => {
    if (mannequinSurfaceSnapshots.size === 0) return undefined;
    const out: Record<string, ModifierGeometry> = {};
    for (const [objectName, target] of sceneObjectsByName) {
      const snapshot = mannequinSurfaceSnapshots.get(target.id);
      if (snapshot) out[objectName] = snapshot.geometry;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }, [sceneObjectsByName, mannequinSurfaceSnapshots]);
  const runtimeSourceCacheKeys = useMemo(() => {
    if (mannequinSurfaceSnapshots.size === 0) return undefined;
    const out = new Map<string, string>();
    for (const [objectName, target] of sceneObjectsByName) {
      const snapshot = mannequinSurfaceSnapshots.get(target.id);
      if (snapshot) out.set(objectName, snapshot.cacheKey);
    }
    return out.size > 0 ? out : undefined;
  }, [sceneObjectsByName, mannequinSurfaceSnapshots]);
  const modifierWorldTransform = useMemo(
    () =>
      needsModifierOwnerWorldTransform
        ? worldTransformForSceneObject(
            modifierSourceObj,
            getCollectionForObject(modifierCollections, modifierSourceObj.id),
          )
        : IDENTITY_WORLD_TRANSFORM,
    [needsModifierOwnerWorldTransform, modifierSourceObj, modifierCollections],
  );
  const modifierOwner = useMemo(
    () => ({
      position: modifierWorldTransform.position,
      rotation: modifierWorldTransform.rotation,
      scale: modifierWorldTransform.scale,
      vertexGroups: modifierSourceObj.vertexGroups,
    }),
    [modifierWorldTransform, modifierSourceObj.vertexGroups],
  );
  const followConstraints = useMemo(
    () =>
      filteredModifiers.filter(
        (m): m is BlendFollowObjectModifier =>
          m.kind === "followObject" && m.enabled && !!m.targetObjectName,
      ),
    [filteredModifiers],
  );
  const followConstraintsRef = useRef(followConstraints);
  followConstraintsRef.current = followConstraints;

  // Resolve source object's full material slot list for the "Use Source Material"
  // toggle. All slots are needed so multi-slot assignments (e.g. a handle with a
  // different material applied to a vertex group) survive the instance pipeline.
  // Derives from resolvedModifiers and sceneObjectsByName (O(1) lookups); the
  // materialsMap subscription below handles live material edits.
  const instanceSourceSlots = useMemo(() => {
    const mod = filteredModifiers.find(
      (m) =>
        m.kind === "instance" &&
        m.enabled &&
        m.useSourceMaterial &&
        m.sourceObjectName,
    );
    if (!mod || mod.kind !== "instance" || !mod.sourceObjectName) return null;
    return sceneObjectsByName.get(mod.sourceObjectName)?.materialSlots ?? null;
  }, [filteredModifiers, sceneObjectsByName]);
  const instanceSourceMaterials: SceneMaterial[] | null = useMemo(() => {
    if (!instanceSourceSlots) return null;
    return instanceSourceSlots.map(
      (slot) =>
        materialsMap.get(slot.materialId)?.properties ?? DEFAULT_MATERIAL,
    );
  }, [instanceSourceSlots, materialsMap]);

  // Build geometry (apply modifiers if any).
  // Without modifiers, vertex positions and normals are updated in-place via
  // useEffect for performance during sculpt strokes. With modifiers, the full
  // geometry is rebuilt because modifiers change vertex/index counts.
  const hasModifiers = displayedGeometryModifiers.length > 0;

  // Resolve per-face slot indices from `faceAttributes.materialIndex`.
  // Returns undefined for single-slot objects (fast path — one draw call).
  // Kept live in edit mode so multi-slot material assignments are visible
  // through the edit overlay as they happen.
  const faceMat = useMemo(
    () => resolveFaceMaterialIndices(renderObj),
    [renderObj],
  );

  const inputModifierGeometry = useMemo<ModifierGeometry | null>(() => {
    if (!hasModifiers) return null;
    return buildRenderModifierGeometry(modifierSourceObj);
  }, [
    hasModifiers,
    modifierSourceObj.vertices,
    modifierSourceObj.indices,
    modifierSourceObj.topology,
    modifierSourceObj.uvs,
    modifierSourceObj.skinRadii,
    modifierSourceObj,
  ]);
  const modifierBaseCacheKey = useMemo(
    () => sceneObjectGeometryCacheKey(modifierSourceObj),
    [
      modifierSourceObj.id,
      modifierSourceObj.vertices,
      modifierSourceObj.indices,
      modifierSourceObj.topology,
      modifierSourceObj.uvs,
      modifierSourceObj.skinRadii,
      modifierSourceObj.vertexGroups,
      modifierSourceObj,
    ],
  );

  const modifierRequest = useMemo(() => {
    if (!inputModifierGeometry) return null;
    return buildModifierEvaluationRequest({
      baseCacheKey: modifierBaseCacheKey,
      geometry: inputModifierGeometry,
      owner: modifierOwner,
      modifiers: displayedGeometryModifiers,
      sceneObjectsByName,
      runtimeSourceGeometries,
      runtimeSourceCacheKeys,
      modifierOverrides: allModifierOverrides,
    });
  }, [
    inputModifierGeometry,
    modifierBaseCacheKey,
    modifierOwner,
    displayedGeometryModifiers,
    sceneObjectsByName,
    runtimeSourceGeometries,
    runtimeSourceCacheKeys,
    allModifierOverrides,
  ]);

  const evaluatedModifierGeometry = useModifierEvaluation(modifierRequest);

  const geometryBundle = useMemo(() => {
    const hasEnabledMods = displayedGeometryModifiers.some((m) => m.enabled);
    let result: THREE.BufferGeometry;
    let structuralSource: {
      positions: Float32Array;
      topology: SceneObject["topology"];
      autoSmoothAngle: number;
    } | null = null;
    const autoSmoothAngle = resolveAutoSmoothAngle(
      obj.shading,
      obj.autoSmoothAngle,
    );

    if (hasEnabledMods && inputModifierGeometry && evaluatedModifierGeometry) {
      const modified = evaluatedModifierGeometry;
      // Modifier evaluation remaps face attributes through face provenance,
      // so generated child faces inherit material slots / shading from their
      // source faces instead of falling back to render-time draw groups.
      const modFaceMat =
        modified.faceAttributes?.materialIndex &&
        modified.topology &&
        modified.topology.offsets.length - 1 ===
          modified.faceAttributes.materialIndex.length
          ? modified.faceAttributes.materialIndex
          : undefined;
      const evaluatedFaceShading = modified.faceAttributes?.shading;
      const modFaceShading =
        evaluatedFaceShading &&
        modified.topology &&
        modified.topology.offsets.length - 1 === evaluatedFaceShading.length
          ? evaluatedFaceShading
          : undefined;
      result = finalizePerCorner(
        modified,
        autoSmoothAngle,
        modFaceMat,
        modFaceShading,
      );
      if (modified.topology) {
        structuralSource = {
          positions: modified.positions,
          topology: modified.topology,
          autoSmoothAngle,
        };
      }
    } else {
      // No modifiers OR cold-start waiting on the first authoritative result:
      // expand the base mesh directly. `useModifierEvaluation` handles
      // stale-while-revalidate on re-eval, so the only window that reaches
      // here with modifiers enabled is before the worker has ever returned.
      const expanded = expandForRendering(
        renderObj.vertices,
        renderObj.loopNormals,
        renderObj.topology,
        renderObj.uvs,
      );
      result = new THREE.BufferGeometry();
      result.setAttribute(
        "position",
        new THREE.BufferAttribute(expanded.renderVertices, 3),
      );
      result.setAttribute(
        "normal",
        new THREE.BufferAttribute(expanded.renderNormals, 3),
      );
      if (expanded.renderUVs) {
        result.setAttribute(
          "uv",
          new THREE.BufferAttribute(expanded.renderUVs, 2),
        );
      }
      if (renderObj.indices.length > 0) {
        result.setIndex(
          new THREE.BufferAttribute(new Uint32Array(renderObj.indices), 1),
        );
      }
      if (faceMat) {
        applyFaceMaterialGroups(result, renderObj.topology, faceMat);
      }
      structuralSource = {
        positions: renderObj.vertices,
        topology: renderObj.topology,
        autoSmoothAngle,
      };
    }

    result.computeBoundingSphere();
    if (structuralSource) {
      result.userData.poserefSurfaceTopology = structuralSource.topology;
      result.userData.poserefSurfacePositions = structuralSource.positions;
      result.userData.poserefTriToFace = getCachedTriToPolyMap(
        structuralSource.topology,
      );
    }
    // BVH is built in a useEffect below so it doesn't block the render frame.
    return { geometry: result, structuralSource };
  }, [
    displayedGeometryModifiers,
    inputModifierGeometry,
    evaluatedModifierGeometry,
    faceMat,
    renderObj.vertices,
    renderObj.indices,
    renderObj.loopNormals,
    renderObj.uvs,
    renderObj.topology,
    obj.shading,
    obj.autoSmoothAngle,
  ]);

  const geometry = geometryBundle.geometry;
  const hasGeometryMaterialGroups = geometry.groups.length > 0;
  const structuralSource = geometryBundle.structuralSource;
  const wireframeSource = useMemo(() => {
    if (isBeingEdited) {
      return { vertices: renderObj.vertices, topology: renderObj.topology };
    }
    if (structuralSource) {
      return {
        vertices: structuralSource.positions,
        topology: structuralSource.topology,
      };
    }
    if (evaluatedModifierGeometry) {
      return {
        vertices: evaluatedModifierGeometry.positions,
        topology: topologyForModifierGeometry(evaluatedModifierGeometry),
      };
    }
    return { vertices: renderObj.vertices, topology: renderObj.topology };
  }, [
    isBeingEdited,
    renderObj.vertices,
    renderObj.topology,
    structuralSource,
    evaluatedModifierGeometry,
  ]);

  // Modifier updates rebuild the main render geometry for scene objects.
  // Without disposing the replaced BufferGeometry, continuous slider scrubs
  // leak one GPU geometry per authoritative result.
  useDispose(geometry);

  // Wire-edge line geometry — shown when no face topology exists and the Skin
  // modifier is absent or disabled (e.g. a freshly-added Skin Armature with
  // its modifier toggled off).  Expands wire edge indices to paired positions
  // so THREE.LineSegments can draw each edge as a line.
  const skinModifierEnabled = displayedGeometryModifiers.some(
    (m) => m.kind === "skin" && m.enabled,
  );
  const hasOnlyWireEdges =
    renderObj.indices.length === 0 && renderObj.topology.edges.length > 0;
  // EditModeRenderer draws its own edge geometry when active — suppress the
  // object-mode wire overlay to avoid double-drawing in edit mode.
  const showWireLineSegments =
    hasOnlyWireEdges && !skinModifierEnabled && !isBeingEdited;

  const wireLineGeo = useMemo(() => {
    if (!showWireLineSegments) return null;
    const wirePairs = getWireEdgePairs(renderObj.topology);
    if (wirePairs.length === 0) return null;
    const posArr = new Float32Array(wirePairs.length * 3);
    for (let i = 0; i < wirePairs.length; i++) {
      const vi = wirePairs[i];
      posArr[i * 3] = renderObj.vertices[vi * 3];
      posArr[i * 3 + 1] = renderObj.vertices[vi * 3 + 1];
      posArr[i * 3 + 2] = renderObj.vertices[vi * 3 + 2];
    }
    const geo = new LineSegmentsGeometry();
    geo.setPositions(posArr);
    return geo;
  }, [showWireLineSegments, renderObj.topology, renderObj.vertices]);

  const wireLineMat = useMemo(() => {
    if (!wireLineGeo) return null;
    return createObjectHelperLineMaterial({
      color: wireLineColor,
      opacity: showHiddenSelectionGhost
        ? HIDDEN_SELECTION_GHOST_OPACITY
        : selected && showSceneInteraction
          ? 0.9
          : 0.6,
      linewidth: WIRE_OBJECT_LINEWIDTH,
      depthTest: !showHiddenSelectionGhost,
    });
  }, [
    wireLineGeo,
    wireLineColor,
    showHiddenSelectionGhost,
    selected,
    showSceneInteraction,
  ]);

  const wireLineSegments = useMemo(() => {
    if (!wireLineGeo || !wireLineMat) return null;
    const lines = new LineSegments2(wireLineGeo, wireLineMat);
    lines.renderOrder = 1;
    return lines;
  }, [wireLineGeo, wireLineMat]);

  useDispose(wireLineGeo);
  useDispose(wireLineMat);

  // Vertex → corners reverse map (CSR format).
  // Built once per unique corners array — stable across edit transforms and
  // sculpt strokes while topology is unchanged. Lets drag previews jump
  // straight to the affected expanded corners instead of rescanning the whole
  // corner buffer every frame.
  const vtxToCorners = useMemo(() => {
    const { corners } = renderObj.topology;
    const vc = renderObj.vertices.length / 3;
    const counts = new Uint32Array(vc);
    for (let c = 0; c < corners.length; c++) counts[corners[c]]++;
    const offsets = new Uint32Array(vc + 1);
    for (let v = 0; v < vc; v++) offsets[v + 1] = offsets[v] + counts[v];
    const data = new Uint32Array(corners.length);
    counts.fill(0);
    for (let c = 0; c < corners.length; c++) {
      const vi = corners[c];
      data[offsets[vi] + counts[vi]] = c;
      counts[vi]++;
    }
    return { offsets, data };
  }, [
    renderObj.topology.corners,
    renderObj.vertices.length,
    renderObj.topology,
  ]);

  // Update vertex positions and normals in-place when they change (no geometry rebuild).
  // Skipped when modifiers are active — the useMemo handles full rebuilds.
  // BVH rebuilds are scheduled asynchronously by the shared bounds-tree manager
  // after these attribute writes land on the live geometry.
  useEffect(() => {
    if (hasModifiers) return;
    // Non-indexed geometry (face-texel sketch mode) has expanded vertex arrays
    // that don't match obj.vertices — skip in-place updates; the useMemo
    // already built correct positions via toNonIndexed().
    if (!geometry.index) return;
    syncExpandedPositionAttribute(
      geometry,
      renderObj.vertices,
      renderObj.topology,
      dirtyEditVertices,
      null,
      vtxToCorners,
      renderObj.loopNormals,
      renderObj.uvs,
    );
    const nrmAttr = geometry.getAttribute("normal") as THREE.BufferAttribute;
    if (
      !hasDirtyEditVertices &&
      nrmAttr &&
      nrmAttr.array.length === renderObj.loopNormals.length
    ) {
      (nrmAttr.array as Float32Array).set(renderObj.loopNormals);
      nrmAttr.updateRanges.length = 0;
      nrmAttr.needsUpdate = true;
    } else if (!hasDirtyEditVertices) {
      geometry.setAttribute(
        "normal",
        new THREE.BufferAttribute(new Float32Array(renderObj.loopNormals), 3),
      );
    }
  }, [
    renderObj.vertices,
    renderObj.loopNormals,
    renderObj.topology,
    renderObj.uvs,
    geometry,
    hasModifiers,
    dirtyEditVertices,
    hasDirtyEditVertices,
    vtxToCorners,
  ]);

  // Live edit commands mutate vertices every frame; keep the existing sphere
  // during the drag and refresh once the command settles so we don't pay an
  // O(vertex-count) scan on every preview frame.
  useEffect(() => {
    if (hasModifiers) return;
    if (isBeingEdited && isCommandActive) return;
    geometry.computeBoundingSphere();
  }, [geometry, hasModifiers, isBeingEdited, isCommandActive]);

  // Hide faces in edit mode: replace the index buffer with a filtered version
  // that excludes triangles belonging to hidden polygon faces.
  const fullIndicesRef = useRef<Uint32Array | null>(null);
  useEffect(() => {
    if (!geometry.index) return;
    if (hiddenFaces && hiddenFaces.size > 0) {
      // Capture the full index buffer on first hide
      if (!fullIndicesRef.current) {
        fullIndicesRef.current = new Uint32Array(renderObj.indices);
      }
      const full = fullIndicesRef.current;
      const triToFace = getCachedTriToPolyMap(renderObj.topology);
      const triCount = full.length / 3;
      const kept: number[] = [];
      for (let t = 0; t < triCount; t++) {
        if (!hiddenFaces.has(triToFace[t])) {
          kept.push(full[t * 3], full[t * 3 + 1], full[t * 3 + 2]);
        }
      }
      const filtered = new Uint32Array(kept);
      geometry.setIndex(new THREE.BufferAttribute(filtered, 1));
      geometry.computeBoundingSphere();
    } else if (fullIndicesRef.current) {
      // Restore the full index buffer
      geometry.setIndex(
        new THREE.BufferAttribute(new Uint32Array(renderObj.indices), 1),
      );
      geometry.computeBoundingSphere();
      fullIndicesRef.current = null;
    }
  }, [hiddenFaces, geometry, renderObj.indices, renderObj.topology]);

  useFrame(() => {
    if (!isSculptTarget || hasModifiers) return;

    // Update geometry attributes from the live CPU buffers.
    const posAttr = geometry.getAttribute("position") as
      | THREE.BufferAttribute
      | undefined;
    const nrmAttr = geometry.getAttribute("normal") as
      | THREE.BufferAttribute
      | undefined;
    const dirty = consumeDirtySculptUpdate();
    const { corners } = renderObj.topology;

    if (dirty) {
      // Partial position update — O(dirty_verts × avg_fan) via reverse map.
      // No longer iterates all corners; jumps directly to affected corners.
      if (posAttr) {
        const posArr = posAttr.array as Float32Array;
        const { offsets: vtcOff, data: vtcData } = vtxToCorners;
        let cMin = corners.length,
          cMax = 0;
        for (const vi of dirty.vertices) {
          const vx = renderObj.vertices[vi * 3];
          const vy = renderObj.vertices[vi * 3 + 1];
          const vz = renderObj.vertices[vi * 3 + 2];
          const cStart = vtcOff[vi];
          const cEnd = vtcOff[vi + 1];
          for (let j = cStart; j < cEnd; j++) {
            const c = vtcData[j];
            posArr[c * 3] = vx;
            posArr[c * 3 + 1] = vy;
            posArr[c * 3 + 2] = vz;
            if (c < cMin) cMin = c;
            if (c > cMax) cMax = c;
          }
        }
        if (cMin <= cMax) {
          posAttr.updateRanges.length = 0;
          posAttr.addUpdateRange(cMin * 3, (cMax - cMin + 1) * 3);
          posAttr.needsUpdate = true;
        }
      }

      // Partial normal upload — bounded by the corner range returned by
      // recompute_loop_normals_partial.  Copies only the [cornerMin, cornerMax]
      // slice of loopNormals instead of the entire buffer.
      if (nrmAttr && dirty.cornerMin <= dirty.cornerMax) {
        const nrmArr = nrmAttr.array as Float32Array;
        const byteStart = dirty.cornerMin * 3;
        const byteCount = (dirty.cornerMax - dirty.cornerMin + 1) * 3;
        nrmArr.set(
          renderObj.loopNormals.subarray(byteStart, byteStart + byteCount),
          byteStart,
        );
        nrmAttr.updateRanges.length = 0;
        nrmAttr.addUpdateRange(byteStart, byteCount);
        nrmAttr.needsUpdate = true;
      }
    } else {
      // Full update — first frame after entering sculpt mode or no dirty data.
      if (posAttr) {
        const posArr = posAttr.array as Float32Array;
        for (let c = 0; c < corners.length; c++) {
          const vi = corners[c];
          posArr[c * 3] = renderObj.vertices[vi * 3];
          posArr[c * 3 + 1] = renderObj.vertices[vi * 3 + 1];
          posArr[c * 3 + 2] = renderObj.vertices[vi * 3 + 2];
        }
        posAttr.updateRanges.length = 0;
        posAttr.needsUpdate = true;
      }
      if (nrmAttr) {
        (nrmAttr.array as Float32Array).set(renderObj.loopNormals);
        nrmAttr.updateRanges.length = 0;
        nrmAttr.needsUpdate = true;
      }
    }
  });

  // Sculpt still refreshes the bounding sphere once on stroke-end. The shared
  // async bounds-tree manager uses sculptStrokeSeq as the rebuild token, so
  // raycasting acceleration is refreshed off the main thread after pointerup.
  const sculptStrokeSeq = useStore((s) => s.sculptStrokeSeq);
  useEffect(() => {
    if (hasModifiers) return;
    geometry.computeBoundingSphere();
  }, [geometry, hasModifiers]);

  const boundsTreeVersion = hasModifiers
    ? geometry
    : isSculptTarget
      ? sculptStrokeSeq
      : renderObj.vertices;
  useAsyncBoundsTree(geometry, boundsTreeVersion, !isBeingEdited);

  // Material — wireframe view mode renders transparent surface beneath PolyWireframe overlay.
  // The memo owns the returned material(s); the useEffect below disposes it on
  // replay or unmount so each primitive holds exclusive ownership.
  //
  // In the standard object-mode PBR path we return a `THREE.Material[]` aligned
  // with `obj.materialSlots` (or the instance source's slots) when multi-slot,
  // so `geometry.groups` routes each triangle run to the right slot. The wireframe
  // override is single-material by design. Edit mode uses the object's true
  // material here; the gray overlay is handled by editOverlayMaterial below.
  const material = useMemo<THREE.Material | THREE.Material[]>(() => {
    // Primary material: prefer slot 0 of the instance source (if active), else the owner's slot 0.
    const mat =
      (instanceSourceMaterials ? instanceSourceMaterials[0] : null) ??
      resolvedMaterial;
    // Auto-smooth normals encode flat/smooth shading in vertex normals.
    // Never set material.flatShading — it computes per-triangle normals
    // from geometry, which reveals the quad→triangle fan diagonal.
    const flat = false;

    // Wireframe mode: primitives use PolyWireframe for clean polygon edges,
    // so the mesh itself is a near-invisible solid surface for raycasting only.
    if (viewMode === "wireframe") {
      const hideWireframeFill = effectiveWireframeXray && !isBeingEdited;
      return new THREE.MeshBasicMaterial({
        color: mat.color,
        transparent: true,
        opacity: hideWireframeFill ? 0 : 0.05,
        side: THREE.DoubleSide,
        colorWrite: !hideWireframeFill,
      });
    }

    const matOpts = {
      viewMode,
      matcapPreset,
      objectXray: objectXrayActive,
      wireframeXray,
      showTextures,
      textureMapsEnabled,
    };
    // Per-primitive default projection for the grid overlay — curved
    // primitives (sphere, cylinder, cone, ...) render in triplanar mode by
    // default; everything else uses tangent. Applies to every material slot
    // on the object, since the hint is derived from the object's kind.
    const gridProj = getDefaultGridProjection(obj.objectKind);

    // Instance-source material override: build a Material[] only when the
    // evaluated geometry has draw groups. Three.js renders Material[] meshes
    // group-by-group; with no groups, the mesh disappears.
    if (instanceSourceMaterials && instanceSourceMaterials.length > 0) {
      if (instanceSourceMaterials.length === 1 || !hasGeometryMaterialGroups) {
        return createViewportMaterial(
          sceneMaterialToConfig(
            instanceSourceMaterials[0],
            flat,
            undefined,
            gridProj,
          ),
          matOpts,
        );
      }
      return instanceSourceMaterials.map((sm) =>
        createViewportMaterial(
          sceneMaterialToConfig(sm, flat, undefined, gridProj),
          matOpts,
        ),
      );
    }

    // Multi-slot path — build a parallel Material[] keyed by slot index so the
    // geometry groups emitted by `applyFaceMaterialGroups` route correctly.
    // Single-slot objects stay on the cheaper one-material code path.
    if (slotMaterials.length > 1 && faceMat && hasGeometryMaterialGroups) {
      return slotMaterials.map((sm) =>
        createViewportMaterial(
          sceneMaterialToConfig(sm, flat, undefined, gridProj),
          matOpts,
        ),
      );
    }

    return createViewportMaterial(
      sceneMaterialToConfig(mat, flat, undefined, gridProj),
      matOpts,
    );
  }, [
    viewMode,
    objectXrayActive,
    effectiveWireframeXray,
    resolvedMaterial,
    instanceSourceMaterials,
    slotMaterials,
    faceMat,
    hasGeometryMaterialGroups,
    matcapPreset,
    wireframeXray,
    showTextures,
    textureMapsEnabled,
    obj.objectKind,
    isBeingEdited,
  ]);

  // Dispose the owned material(s) when it is replaced or the primitive unmounts.
  // Material arrays come from the multi-slot path; each entry is owned by this
  // memo and must be disposed independently (SceneRenderer uses the same shape).
  useEffect(
    () => () => {
      if (Array.isArray(material)) {
        for (const m of material) m.dispose();
      } else {
        material.dispose();
      }
    },
    [material],
  );

  // ---------------------------------------------------------------------------
  // Edit mode overlay — near-opaque gray mesh rendered on top of the true
  // material so the object's original color faintly bleeds through the tint.
  // X-ray mode drops the opacity to SURFACE_XRAY_OPACITY so the mesh becomes
  // see-through as expected. Wireframe viewport mode is treated as matcap here
  // since there is no meaningful lighting model to use for the overlay.
  // ---------------------------------------------------------------------------

  const editOverlayMaterial = useMemo<
    THREE.MeshMatcapMaterial | THREE.MeshPhysicalMaterial | null
  >(() => {
    if (!isBeingEdited) return null;
    const opacity = editXray ? SURFACE_XRAY_OPACITY : EDIT_OVERLAY_OPACITY;
    const overlayViewMode = viewMode === "wireframe" ? "matcap" : viewMode;
    if (overlayViewMode === "matcap") {
      const preset = MATCAP_PRESETS[matcapPreset];
      return new THREE.MeshMatcapMaterial({
        color: "#808080",
        matcap: preset?.create(),
        side: THREE.DoubleSide,
        transparent: true,
        opacity,
        depthWrite: false,
      });
    }
    // material — lit PBR with Blender-style defaults
    return new THREE.MeshPhysicalMaterial({
      color: "#808080",
      roughness: 0.4,
      metalness: 0.0,
      side: THREE.DoubleSide,
      transparent: true,
      opacity,
      depthWrite: false,
    });
  }, [isBeingEdited, editXray, viewMode, matcapPreset]);

  useEffect(
    () => () => {
      editOverlayMaterial?.dispose();
    },
    [editOverlayMaterial],
  );

  // Determine which polygon wireframe overlays to show
  const showEditWire = isBeingEdited;
  const showWireframeMode = !isBeingEdited && viewMode === "wireframe";
  const showPrimitiveSymmetryDebug =
    !isBeingEdited &&
    viewMode === "wireframe" &&
    primitiveSymmetryDebugAxis !== null;
  const showSelectionPrepass =
    (selected || eyedropperHovered) &&
    !isBeingEdited &&
    effectiveVisible &&
    showSceneInteraction &&
    !isSculptTarget &&
    !isSketchTarget;
  // Edit mode adds a depth prepass so the scene grid is occluded by the mesh
  // being edited. The edit overlay has depthWrite:false, which would otherwise
  // leave the depth buffer unfilled, letting the grid bleed through at ~8%
  // opacity. Skip the prepass only in edit x-ray mode where see-through is intentional.
  const needsDepthPrepass =
    (showWireframeMode && !effectiveWireframeXray) ||
    (isBeingEdited && !editXray);
  const isInGeneratorCollection = useStore((s) => {
    for (const col of s.collections.values()) {
      if (col.generatorRef && col.objectIds.has(obj.id)) return true;
    }
    return false;
  });
  const generatorAdjusting = useStore((s) => s.generatorAdjusting);
  const isGeneratorBacked = obj.generator != null || isInGeneratorCollection;
  const showWireframeOverlay = shouldShowGeneratorWireframeOverlay({
    isBeingEdited,
    showWireframe,
    generatorAdjusting,
    isGeneratorBacked,
    viewMode,
  });
  const asymmetricWireEdges = useMemo(() => {
    if (!showPrimitiveSymmetryDebug || primitiveSymmetryDebugAxis == null)
      return null;
    return findAsymmetricTopologyEdges(
      wireframeSource.vertices,
      wireframeSource.topology,
      primitiveSymmetryDebugAxis,
    );
  }, [showPrimitiveSymmetryDebug, primitiveSymmetryDebugAxis, wireframeSource]);
  const selectionVariant = modifierStackHasEnabledInstance(
    displayedGeometryModifiers,
  )
    ? "instance"
    : isInGeneratorCollection
      ? "generator"
      : "default";
  const selectionPrepassMaterial = useMemo(
    () =>
      getSelectionPrepassMaterial(
        obj.id,
        selectionVariant,
        eyedropperHovered || obj.id === activeSelectedObjectId,
      ),
    [obj.id, selectionVariant, eyedropperHovered, activeSelectedObjectId],
  );

  useEffect(() => {
    if (meshRef.current) markInkingCategory(meshRef.current, "primitive");
  }, []);

  useSelectionOutlineLayer(selectionPrepassRef, showSelectionPrepass);

  const renderVisibleInViewport = effectiveVisible || showHiddenSelectionGhost;

  return (
    <group
      ref={groupRef}
      position={worldTransform.position}
      rotation={worldTransform.rotation}
      scale={worldTransform.scale}
      visible={renderVisibleInViewport}
    >
      {effectiveVisible && needsDepthPrepass && (
        <mesh
          geometry={geometry}
          material={WIREFRAME_DEPTH_PREPASS_MAT}
          raycast={noRaycast}
          renderOrder={-1}
        />
      )}
      <mesh
        ref={meshRef}
        name={obj.id}
        userData={{
          poserefObjectId: obj.id,
          poserefInkingInclude: "primitive",
        }}
        geometry={geometry}
        material={material}
        visible={effectiveVisible}
        castShadow={shadowsEnabled}
        receiveShadow={shadowsEnabled}
        raycast={
          effectiveVisible && !isBeingEdited ? acceleratedRaycast : noRaycast
        }
        onPointerDown={isBeingEdited ? undefined : handlePointerDown}
        onPointerUp={isBeingEdited ? undefined : handlePointerUp}
        onPointerEnter={isBeingEdited ? undefined : handlePointerEnter}
        onPointerLeave={isBeingEdited ? undefined : handlePointerLeave}
      />
      {showHiddenSelectionGhost && (
        <mesh
          geometry={geometry}
          material={HIDDEN_SELECTION_GHOST_MAT}
          raycast={noRaycast}
          renderOrder={HIDDEN_SELECTION_GHOST_RENDER_ORDER}
          userData={{ excludeFromExport: true }}
        />
      )}

      {/* Edit mode overlay — near-opaque gray tint; lets the true material faintly show through */}
      {effectiveVisible && editOverlayMaterial && (
        <mesh
          geometry={geometry}
          material={editOverlayMaterial}
          raycast={noRaycast}
          renderOrder={1}
          userData={{ excludeFromExport: true }}
        />
      )}

      {/* Polygon wireframe overlays and selection outline */}
      {effectiveVisible && showEditWire && (
        <PolyWireframe
          objectId={obj.id}
          vertices={renderObj.vertices}
          topology={renderObj.topology}
          color="#88aacc"
          opacity={0.3}
          renderOrder={1}
          hiddenFaces={hiddenFaces}
          dirtyVertices={dirtyEditVertices}
        />
      )}
      {effectiveVisible && showWireframeOverlay && (
        <PolyWireframe
          vertices={wireframeSource.vertices}
          topology={wireframeSource.topology}
          color="#ffffff"
          opacity={0.15}
          depthTest={!effectiveWireframeXray}
          renderOrder={1}
        />
      )}
      {effectiveVisible && showWireframeMode && (
        <PolyWireframe
          vertices={wireframeSource.vertices}
          topology={wireframeSource.topology}
          color={resolvedMaterial.color}
          opacity={1.0}
          depthTest={!effectiveWireframeXray}
          renderOrder={1}
        />
      )}
      {effectiveVisible &&
        showPrimitiveSymmetryDebug &&
        asymmetricWireEdges &&
        asymmetricWireEdges.size > 0 && (
          <PolyWireframe
            vertices={wireframeSource.vertices}
            topology={wireframeSource.topology}
            color="#ff4444"
            opacity={1}
            depthTest={!effectiveWireframeXray}
            renderOrder={2}
            onlyEdges={asymmetricWireEdges}
          />
        )}
      {effectiveVisible &&
        showPrimitiveSymmetryDebug &&
        primitiveSymmetryDebugAxis && (
          <SymmetryPlaneOverlay axes={[primitiveSymmetryDebugAxis]} />
        )}
      {/* Sculpt dim overlay — darkens this object when another object is being sculpted */}
      {effectiveVisible && isSculptDimmed && (
        <mesh
          geometry={geometry}
          material={SCULPT_DIM_MAT}
          raycast={noRaycast}
          renderOrder={3}
          userData={{ excludeFromExport: true }}
        />
      )}
      {showSelectionPrepass && (
        <mesh
          ref={selectionPrepassRef}
          geometry={geometry}
          material={selectionPrepassMaterial}
          raycast={noRaycast}
          userData={{ excludeFromExport: true }}
        />
      )}
      {curveObject && obj.curveData && (
        <CurveLineOverlay
          curveData={obj.curveData}
          color={curveHelperColor}
          opacity={
            showHiddenSelectionGhost
              ? HIDDEN_SELECTION_GHOST_OPACITY
              : selected && showSceneInteraction
                ? 0.8
                : 0.5
          }
          depthTest={!showHiddenSelectionGhost}
        />
      )}
      {curveObject &&
        obj.curveData &&
        !obj.curveData.bevel.enabled &&
        effectiveVisible &&
        !isBeingEdited && (
          <CurveHitTargets
            objectId={obj.id}
            curveData={obj.curveData}
            handlePointerDown={handlePointerDown}
            handlePointerUp={handlePointerUp}
          />
        )}
      {wireLineSegments && (
        <>
          <primitive
            object={wireLineSegments}
            userData={{ excludeFromExport: true }}
            raycast={noRaycast}
          />
          {effectiveVisible && !isBeingEdited && (
            <WireObjectHitTargets
              objectId={obj.id}
              vertices={renderObj.vertices}
              topology={renderObj.topology}
              handlePointerDown={handlePointerDown}
              handlePointerUp={handlePointerUp}
            />
          )}
        </>
      )}
      {isBeingEdited && (
        <SkinRadiusOverlay
          objectId={obj.id}
          resolvedModifiers={geometryModifiers}
        />
      )}
      <LiveEditGeometrySync
        objectId={isBeingEdited ? obj.id : null}
        geometry={geometry}
        vertices={renderObj.vertices}
        topology={renderObj.topology}
        loopNormals={renderObj.loopNormals}
        uvs={renderObj.uvs}
        vtxToCorners={vtxToCorners}
        disabled={hasModifiers}
      />
    </group>
  );
}

function WireObjectHitTargets({
  objectId,
  vertices,
  topology,
  handlePointerDown,
  handlePointerUp,
}: {
  objectId: string;
  vertices: Float32Array;
  topology: SceneObject["topology"];
  handlePointerDown: (e: ThreeEvent<PointerEvent>) => void;
  handlePointerUp: (e: ThreeEvent<PointerEvent>) => void;
}) {
  const wirePairs = useMemo(() => getWireEdgePairs(topology), [topology]);
  const wireHits = useMemo(() => {
    const hits: Array<{ key: string; v0: number; v1: number }> = [];
    for (let i = 0; i < wirePairs.length; i += 2) {
      const v0 = wirePairs[i]!;
      const v1 = wirePairs[i + 1]!;
      hits.push({ key: `${objectId}-wire-hit-${v0}-${v1}`, v0, v1 });
    }
    return hits;
  }, [objectId, wirePairs]);

  if (wireHits.length === 0) return null;

  return (
    <>
      {wireHits.map((hit) => (
        <WireEdgeHitMesh
          key={hit.key}
          name={objectId}
          v0={hit.v0}
          v1={hit.v1}
          vertices={vertices}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          userData={{ excludeFromExport: true }}
        />
      ))}
    </>
  );
}

function CurveHitTargets({
  objectId,
  curveData,
  handlePointerDown,
  handlePointerUp,
}: {
  objectId: string;
  curveData: CurveData;
  handlePointerDown: (e: ThreeEvent<PointerEvent>) => void;
  handlePointerUp: (e: ThreeEvent<PointerEvent>) => void;
}) {
  // Flatten evaluated positions to the [x,y,z,...] layout WireEdgeHitMesh expects,
  // then drop one invisible cylinder per spline segment so the bare line is pickable.
  const flatPositions = useMemo(() => {
    const { positions } = evaluateCurve(curveData);
    const arr = new Float32Array(positions.length * 3);
    for (let i = 0; i < positions.length; i++) {
      arr[i * 3] = positions[i][0];
      arr[i * 3 + 1] = positions[i][1];
      arr[i * 3 + 2] = positions[i][2];
    }
    return arr;
  }, [curveData]);

  const segCount = Math.max(0, flatPositions.length / 3 - 1);
  const hitSegments = useMemo(() => {
    return Array.from({ length: segCount }, (_, i) => ({
      key: `${objectId}-curve-hit-${i}-${i + 1}`,
      v0: i,
      v1: i + 1,
    }));
  }, [objectId, segCount]);

  if (hitSegments.length === 0) return null;

  return (
    <>
      {hitSegments.map((hit) => (
        <WireEdgeHitMesh
          key={hit.key}
          name={objectId}
          v0={hit.v0}
          v1={hit.v1}
          vertices={flatPositions}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          userData={{ excludeFromExport: true }}
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Curve spline line overlay
// ---------------------------------------------------------------------------

function CurveLineOverlay({
  curveData,
  color,
  opacity,
  depthTest = true,
}: {
  curveData: CurveData;
  color: string;
  opacity: number;
  depthTest?: boolean;
}) {
  // Keep a single BufferGeometry and Line across drag ticks. Mutate the
  // position attribute in place when curveData changes; only resize (and
  // replace the attribute) when the sample count changes.
  const geometry = useMemo(() => new LineSegmentsGeometry(), []);

  useEffect(() => {
    const { positions } = evaluateCurve(curveData);
    setFatLinePositions(geometry, buildPolylineSegmentPositions(positions));
  }, [curveData, geometry]);

  const material = useMemo(
    () =>
      createObjectHelperLineMaterial({
        color,
        opacity,
        linewidth: OBJECT_HELPER_LINEWIDTH,
        depthTest,
      }),
    [color, opacity, depthTest],
  );

  // Stable Line instance — recreated only when geometry or material change.
  // Without this, `new THREE.Line(...)` fired every render, churning the
  // scene graph node on every drag tick.
  const line = useMemo(() => {
    const fatLine = new LineSegments2(geometry, material);
    fatLine.renderOrder = 1;
    return fatLine;
  }, [geometry, material]);

  useDispose(geometry);
  useDispose(material);

  return (
    <group userData={{ excludeFromExport: true }}>
      <primitive object={line} renderOrder={1} />
    </group>
  );
}

// ---------------------------------------------------------------------------
// Light node — renders a light gizmo + actual Three.js light
// ---------------------------------------------------------------------------

export function LightNode({
  obj,
  shadowMapSize,
}: {
  obj: SceneObject;
  shadowMapSize: number;
}) {
  const selected = useSelectedLeafObjectIds().has(obj.id);
  const workspaceMode = useStore((s) => s.workspaceMode);
  const visible = useStore((s) => s.visibility.get(obj.id) ?? true);
  const renderVisible = useStore((s) => s.renderVisibility.get(obj.id) ?? true);
  const { viewMode, shadowsEnabled } = useViewportShading();
  const shadowSoftness = useActiveRenderConfig((c) => c.shadowSoftness);
  const light = obj.light!;
  const kind = obj.objectKind as LightKind;
  // Snapshot pattern: read parent-chain transform once at render time via
  // `useStore.getState()`, with deps only on the object's own TRS. The
  // per-frame `useFrame` below keeps the rendered group + dir-light
  // target current under parent collection drags. Dropping the full-map
  // subscription removes a major source of cross-object render churn:
  // moving any other scene object no longer re-renders this LightNode.
  const worldTransform = useMemo(
    () =>
      getSceneObjectWorldTransform(
        useStore.getState().sceneObjects,
        useStore.getState().collections,
        obj.id,
      ) ?? {
        position: obj.position,
        rotation: obj.rotation,
        scale: obj.scale,
      },
    [obj.id, obj.position, obj.rotation, obj.scale],
  );
  const isMaterial = viewMode === "material";
  const effectiveVisible = isVisibleInWorkspace({
    workspaceMode,
    viewportVisible: visible,
    renderVisible,
  });
  const showSceneInteraction = viewportSupportsSceneInteraction(workspaceMode);
  const showHiddenSelectionGhost = shouldShowHiddenSelectionGhost({
    selected,
    effectiveVisible,
    showSceneInteraction,
  });
  const renderVisibleInViewport = effectiveVisible || showHiddenSelectionGhost;
  const dirLightRef = useRef<THREE.DirectionalLight>(null);
  const lightGroupRef = useRef<THREE.Group>(null);
  const shadowSettings = useMemo(
    () =>
      resolveRealtimeLightShadowSettings(
        kind,
        shadowsEnabled,
        shadowSoftness,
        shadowMapSize,
      ),
    [kind, shadowsEnabled, shadowSoftness, shadowMapSize],
  );

  // Per-frame transform sync for the light gizmo group AND the directional
  // light's shadow target. Both must track parent collection drags, but
  // neither needs reactive re-renders — Three.js consumes them every
  // frame via the imperative refs below.
  //
  // Directional-light target: light shines along local -Z, so target =
  // worldPos + worldRotate(-Z). Target is in world space because
  // Three.js evaluates `DirectionalLight.target.position` in world space.
  const objId = obj.id;
  const dirTargetScratch = useMemo(() => new THREE.Vector3(), []);
  const dirTargetEuler = useMemo(() => new THREE.Euler(), []);
  useFrame(() => {
    const g = lightGroupRef.current;
    if (!g) return;
    const latestState = useStore.getState();
    if (
      !applySceneObjectWorldTransformToObject3D(
        latestState.sceneObjects,
        latestState.collections,
        objId,
        g,
      )
    )
      return;
    const dirLight = dirLightRef.current;
    if (dirLight) {
      // g.rotation/g.position now reflect world-space transform, so
      // re-derive target from those rather than re-reading store state.
      dirTargetScratch
        .set(0, 0, -1)
        .applyEuler(dirTargetEuler.copy(g.rotation));
      dirLight.target.position.set(
        g.position.x + dirTargetScratch.x,
        g.position.y + dirTargetScratch.y,
        g.position.z + dirTargetScratch.z,
      );
      dirLight.target.updateMatrixWorld();
    }
  });

  // Tap → select. Goes through the shared useObjectTap so lights participate
  // in Blender-style z-depth click-through along with every other object.
  const {
    handlePointerDown,
    handlePointerUp,
    handlePointerEnter,
    handlePointerLeave,
  } = useObjectTap(obj.id);
  const eyedropperHovered = useStore(
    (s) => s.eyedropperSession !== null && s.eyedropperHoveredId === obj.id,
  );
  const highlighted = selected || eyedropperHovered;

  const gizmoColor = useMemo(() => new THREE.Color(light.color), [light.color]);

  const gizmoMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: gizmoColor,
        transparent: showHiddenSelectionGhost,
        opacity: showHiddenSelectionGhost ? HIDDEN_SELECTION_GHOST_OPACITY : 1,
        depthTest: !showHiddenSelectionGhost,
        depthWrite: false,
      }),
    [gizmoColor, showHiddenSelectionGhost],
  );

  const rayMaterial = useMemo(
    () =>
      new THREE.Line2NodeMaterial({
        color: gizmoColor.getHex(),
        linewidth: 3,
        transparent: true,
        opacity: showHiddenSelectionGhost
          ? HIDDEN_SELECTION_GHOST_OPACITY
          : highlighted
            ? 0.7
            : 0.35,
        depthTest: !showHiddenSelectionGhost,
        depthWrite: false,
      }),
    [gizmoColor, highlighted, showHiddenSelectionGhost],
  );

  const outlineMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: "#5aA0e8",
        transparent: true,
        opacity: showHiddenSelectionGhost ? 0.15 : 0.3,
        depthTest: !showHiddenSelectionGhost,
        depthWrite: false,
        side: THREE.BackSide,
      }),
    [showHiddenSelectionGhost],
  );

  // Radial line geometry — 12 short rays extending from center
  const rayGeometry = useMemo(() => {
    const RAY_COUNT = 12;
    const INNER = 0.028;
    const OUTER = 0.045;
    const positions: number[] = [];
    for (let i = 0; i < RAY_COUNT; i++) {
      const angle = (i / RAY_COUNT) * Math.PI * 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      positions.push(cos * INNER, sin * INNER, 0);
      positions.push(cos * OUTER, sin * OUTER, 0);
    }
    const geo = new LineSegmentsGeometry();
    geo.setPositions(positions);
    return geo;
  }, []);

  // Directional light gizmo: a ring with parallel lines shooting in -Z
  const dirGizmoGeometry = useMemo(() => {
    const RING_SEGS = 24;
    const RING_R = DIR_LIGHT_RING_R;
    const LINE_COUNT = 5; // central + 4 around
    const LINE_LEN = 0.05;
    const LINE_START = 0.005; // small gap from ring
    const ARROW_LEN = 0.012;
    const ARROW_HALF = 0.006;
    const positions: number[] = [];

    // Ring in XY plane
    for (let i = 0; i < RING_SEGS; i++) {
      const a0 = (i / RING_SEGS) * Math.PI * 2;
      const a1 = ((i + 1) / RING_SEGS) * Math.PI * 2;
      positions.push(Math.cos(a0) * RING_R, Math.sin(a0) * RING_R, 0);
      positions.push(Math.cos(a1) * RING_R, Math.sin(a1) * RING_R, 0);
    }

    // Parallel lines along -Z
    const offsets: [number, number][] = [[0, 0]];
    const SPREAD = RING_R * 0.6;
    for (let i = 0; i < LINE_COUNT - 1; i++) {
      const a = (i / (LINE_COUNT - 1)) * Math.PI * 2;
      offsets.push([Math.cos(a) * SPREAD, Math.sin(a) * SPREAD]);
    }
    for (const [ox, oy] of offsets) {
      const z0 = -LINE_START;
      const z1 = -(LINE_START + LINE_LEN);
      // Shaft
      positions.push(ox, oy, z0);
      positions.push(ox, oy, z1);
      // Arrowhead: two chevron lines from tip
      positions.push(ox, oy, z1);
      positions.push(ox - ARROW_HALF, oy, z1 + ARROW_LEN);
      positions.push(ox, oy, z1);
      positions.push(ox + ARROW_HALF, oy, z1 + ARROW_LEN);
    }

    const geo = new LineSegmentsGeometry();
    geo.setPositions(positions);
    return geo;
  }, []);

  return (
    <group
      ref={lightGroupRef}
      name={obj.id}
      position={worldTransform.position}
      rotation={worldTransform.rotation}
      scale={worldTransform.scale}
      visible={renderVisibleInViewport}
    >
      {/* Actual Three.js lights (only in material mode) */}
      {effectiveVisible && isMaterial && kind === "point_light" && (
        <pointLight
          key={shadowSettings.resourceKey}
          color={light.color}
          intensity={light.intensity}
          decay={2}
          castShadow={shadowSettings.castShadow}
          shadow-mapSize={shadowSettings.mapSize}
          shadow-bias={shadowSettings.bias}
          shadow-normalBias={shadowSettings.normalBias}
          shadow-radius={shadowSettings.radius}
        />
      )}
      {effectiveVisible && isMaterial && kind === "directional_light" && (
        <directionalLight
          key={shadowSettings.resourceKey}
          ref={dirLightRef}
          // Override DirectionalLight's default local (0,1,0) so the
          // light is centered on the group; otherwise the shader's
          // direction (light.position − target.position) ends up tilted
          // by +Y relative to the gizmo arrows, which point in local −Z.
          position={[0, 0, 0]}
          color={light.color}
          intensity={light.intensity}
          castShadow={shadowSettings.castShadow}
          shadow-mapSize={shadowSettings.mapSize}
          shadow-camera-left={shadowSettings.camera?.left}
          shadow-camera-right={shadowSettings.camera?.right}
          shadow-camera-top={shadowSettings.camera?.top}
          shadow-camera-bottom={shadowSettings.camera?.bottom}
          shadow-camera-near={shadowSettings.camera?.near}
          shadow-camera-far={shadowSettings.camera?.far}
          shadow-bias={shadowSettings.bias}
          shadow-normalBias={shadowSettings.normalBias}
          shadow-radius={shadowSettings.radius}
        />
      )}
      {/* Visual gizmo */}
      <group
        onPointerDown={effectiveVisible ? handlePointerDown : undefined}
        onPointerUp={effectiveVisible ? handlePointerUp : undefined}
        onPointerEnter={effectiveVisible ? handlePointerEnter : undefined}
        onPointerLeave={effectiveVisible ? handlePointerLeave : undefined}
        userData={{ excludeFromExport: true }}
      >
        {kind === "directional_light" ? (
          <ScreenSpaceGroup targetPx={DIR_GIZMO_PX}>
            {/* Filled disc — solid light-color swatch inside the ring */}
            <mesh material={gizmoMaterial} renderOrder={999}>
              <circleGeometry args={[DIR_LIGHT_RING_R, 24]} />
            </mesh>
            {/* Ring + parallel arrows showing light direction */}
            <DirectionalLightGizmo
              geometry={dirGizmoGeometry}
              material={rayMaterial}
            />
          </ScreenSpaceGroup>
        ) : (
          <ScreenSpaceGroup targetPx={LIGHT_GIZMO_PX}>
            {/* Center sphere */}
            <mesh material={gizmoMaterial} renderOrder={999}>
              <sphereGeometry args={[0.02, 12, 8]} />
            </mesh>
            {highlighted && (
              <mesh material={outlineMaterial} renderOrder={998}>
                <sphereGeometry args={[0.03, 12, 8]} />
              </mesh>
            )}
            {/* Camera-facing radial lines */}
            <LightRays geometry={rayGeometry} material={rayMaterial} />
          </ScreenSpaceGroup>
        )}
      </group>
    </group>
  );
}

/** Target screen-space diameter for light gizmos in pixels. */
const LIGHT_GIZMO_PX = 28;
/** Directional light gizmo is slightly larger to fit ring + arrows. */
const DIR_GIZMO_PX = 38;
/** Base world-space diameter the gizmo geometry spans (outer ray tip × 2). */
const LIGHT_GIZMO_BASE_SIZE = 0.045 * 2;
/** Radius of the directional-light ring in world units (used by geometry and fill disc). */
const DIR_LIGHT_RING_R = 0.035;

/**
 * Scales its children so that LIGHT_GIZMO_BASE_SIZE world units always
 * appears as `targetPx` pixels on screen, regardless of camera distance.
 */
function ScreenSpaceGroup({
  targetPx,
  children,
}: {
  targetPx: number;
  children: React.ReactNode;
}) {
  const ref = useRef<THREE.Group>(null);
  const { camera, size: viewportSize } = useThree();
  const worldPosRef = useRef(new THREE.Vector3());

  useFrame(() => {
    const group = ref.current;
    if (!group) return;
    group.getWorldPosition(worldPosRef.current);
    const desiredWorldSize = screenToWorldRadius(
      targetPx,
      worldPosRef.current,
      camera,
      viewportSize.height,
    );
    const s = desiredWorldSize / LIGHT_GIZMO_BASE_SIZE;
    group.scale.setScalar(s);
  });

  return <group ref={ref}>{children}</group>;
}

/** Camera-facing radial lines for light gizmos (fat lines with round caps). */
function LightRays({
  geometry,
  material,
}: {
  geometry: LineSegmentsGeometry;
  material: THREE.Line2NodeMaterial;
}) {
  const ref = useRef<THREE.Group>(null);
  const { camera } = useThree();

  const lineSegments = useMemo(() => {
    const ls = new LineSegments2(geometry, material);
    ls.renderOrder = 999;
    return ls;
  }, [geometry, material]);

  useFrame(() => {
    if (ref.current) {
      ref.current.quaternion.copy(camera.quaternion);
    }
  });

  return (
    <group ref={ref}>
      <primitive object={lineSegments} />
    </group>
  );
}

/**
 * Directional light gizmo: ring + parallel arrows.
 * Unlike the point-light gizmo the lines are NOT camera-facing — the group
 * inherits the object rotation set on the parent so the arrows visually
 * point in the light's actual direction.
 */
function DirectionalLightGizmo({
  geometry,
  material,
}: {
  geometry: LineSegmentsGeometry;
  material: THREE.Line2NodeMaterial;
}) {
  const lineSegments = useMemo(() => {
    const ls = new LineSegments2(geometry, material);
    ls.renderOrder = 999;
    return ls;
  }, [geometry, material]);

  return <primitive object={lineSegments} />;
}

// ---------------------------------------------------------------------------
// Empty node — plain axes cross (transform-only helper object)
// ---------------------------------------------------------------------------

/** Half-length of each arm in world units. */
const EMPTY_HALF = 0.5;

/** Static positions for the 6-arm axes cross: ±X, ±Y, ±Z. */
const EMPTY_AXIS_POSITIONS = new Float32Array([
  -EMPTY_HALF,
  0,
  0,
  EMPTY_HALF,
  0,
  0,
  0,
  -EMPTY_HALF,
  0,
  0,
  EMPTY_HALF,
  0,
  0,
  0,
  -EMPTY_HALF,
  0,
  0,
  EMPTY_HALF,
]);

export function EmptyNode({ obj }: { obj: SceneObject }) {
  const selected = useSelectedLeafObjectIds().has(obj.id);
  const visible = useStore((s) => s.visibility.get(obj.id) ?? true);
  const workspaceMode = useStore((s) => s.workspaceMode);
  const renderVisible = useStore((s) => s.renderVisibility.get(obj.id) ?? true);
  const showSceneInteraction = viewportSupportsSceneInteraction(workspaceMode);
  const effectiveVisible = isVisibleInWorkspace({
    workspaceMode,
    viewportVisible: visible,
    renderVisible,
  });
  const showHiddenSelectionGhost = shouldShowHiddenSelectionGhost({
    selected,
    effectiveVisible,
    showSceneInteraction,
  });
  const renderVisibleInViewport = effectiveVisible || showHiddenSelectionGhost;
  const groupRef = useRef<THREE.Group>(null);
  const objId = obj.id;
  // Snapshot pattern: only the initial render needs `worldTransform` for
  // the JSX position/rotation/scale; the per-frame `useFrame` below
  // overwrites the group's transform every frame from the latest store
  // state. Subscribing to `sceneObjects` + `collections` here would
  // re-render this node on every unrelated transform change.
  const worldTransform = useMemo(
    () =>
      getSceneObjectWorldTransform(
        useStore.getState().sceneObjects,
        useStore.getState().collections,
        obj.id,
      ) ?? {
        position: obj.position,
        rotation: obj.rotation,
        scale: obj.scale,
      },
    [obj.id, obj.position, obj.rotation, obj.scale],
  );

  // Per-frame transform sync — bypasses React batching during gizmo drags.
  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    const latestState = useStore.getState();
    applySceneObjectWorldTransformToObject3D(
      latestState.sceneObjects,
      latestState.collections,
      objId,
      g,
    );
  });

  // Tap → select. Shared with the rest of the viewport so empties participate
  // in double-tap detection (future) and z-depth click-through cycling.
  const {
    handlePointerDown,
    handlePointerUp,
    handlePointerEnter,
    handlePointerLeave,
  } = useObjectTap(obj.id);
  const eyedropperHovered = useStore(
    (s) => s.eyedropperSession !== null && s.eyedropperHoveredId === obj.id,
  );

  // Axes geometry — 6 segments (±X, ±Y, ±Z), shared across renders
  const axesGeo = useMemo(() => {
    const geo = new LineSegmentsGeometry();
    geo.setPositions(EMPTY_AXIS_POSITIONS);
    return geo;
  }, []);

  // Empties need an explicit line color because they don't get a mesh outline.
  const linesMat = useMemo(
    () =>
      createObjectHelperLineMaterial({
        color: getEmptyHelperColor(selected, eyedropperHovered),
        opacity: showHiddenSelectionGhost ? HIDDEN_SELECTION_GHOST_OPACITY : 1,
        linewidth: OBJECT_HELPER_LINEWIDTH,
        depthTest: !showHiddenSelectionGhost,
        toneMapped: false,
      }),
    [selected, eyedropperHovered, showHiddenSelectionGhost],
  );

  // Stable LineSegments object — recreated only when selection state changes
  const axesLines = useMemo(() => {
    const ls = new LineSegments2(axesGeo, linesMat);
    ls.renderOrder = 999;
    return ls;
  }, [axesGeo, linesMat]);

  useDispose(axesGeo);
  useDispose(linesMat);

  // Small invisible sphere for pointer hit detection
  const hitGeo = useMemo(() => new THREE.SphereGeometry(0.2, 4, 3), []);
  const hitMat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    [],
  );

  if (!renderVisibleInViewport || !showSceneInteraction) return null;

  return (
    <group
      ref={groupRef}
      position={worldTransform.position}
      rotation={worldTransform.rotation as any}
      scale={worldTransform.scale}
      name={obj.id}
    >
      <primitive object={axesLines} />
      {effectiveVisible && (
        <mesh
          geometry={hitGeo}
          material={hitMat}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        />
      )}
    </group>
  );
}
